// ui/ShareModal.js
import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  ShareIcon,
  DocumentDuplicateIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { logError } from "../utils/logger";

/**
 * Modal for sharing a note
 * Provides copy link functionality
 *
 * @param {boolean} open - Controls modal visibility
 * @param {function} onClose - Called when user closes the modal
 * @param {Object} note - The note to share
 */
export default function ShareModal({ open, onClose, note }) {
  const [copyState, setCopyState] = useState("idle"); // 'idle' | 'copied'

  // Generate shareable URL
  const shareUrl = note?.id ? `${window.location.origin}/n/${note.id}` : "";

  // Reset copy state when modal closes
  useEffect(() => {
    if (!open) {
      setCopyState("idle");
    }
  }, [open]);

  // Handle copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState("copied");

      // Reset to idle after 2 seconds
      setTimeout(() => {
        setCopyState("idle");
      }, 2000);
    } catch (err) {
      logError("Failed to copy to clipboard:", err);
    }
  }, [shareUrl]);

  if (!open) return null;

  const isCopied = copyState === "copied";

  const modal = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Modal Card */}
      <div className="relative w-full max-w-md rounded-2xl bg-surface-elevated shadow-2xl overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-3 border-b border-border bg-primary">
          <div className="flex items-center gap-2 text-text-inverse">
            <ShareIcon className="h-5 w-5" />
            <h3 className="text-sm font-semibold">Share Note</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-primary-100"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5 text-text-inverse" />
          </button>
        </header>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Label */}
          <p className="text-sm font-medium text-text-secondary">
            Share this note
          </p>

          {/* Link input with embedded copy button */}
          <div className="relative">
            <input
              type="text"
              readOnly
              value={shareUrl}
              className="w-full border border-border-strong rounded-lg px-3 py-2 pr-20 text-sm text-text-primary bg-surface focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent select-all"
              onClick={(e) => e.target.select()}
            />

            {/* Copy button inside input */}
            <button
              onClick={handleCopy}
              className={`absolute right-1 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                isCopied
                  ? "bg-success-50 text-success hover:bg-success-50"
                  : "bg-primary text-text-inverse hover:bg-primary-hover"
              }`}
              disabled={isCopied}
            >
              {isCopied ? (
                <>
                  <CheckIcon className="h-3.5 w-3.5" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>

          {/* Future: Share with followers section */}
          {/*
          <div className="pt-4 border-t border">
            <p className="text-sm font-medium text-text-secondary mb-3">
              Share with followers
            </p>
            <div className="flex items-center gap-2">
              {followers.map(follower => (
                <button key={follower.id} className="...">
                  <Avatar user={follower} size="md" />
                </button>
              ))}
            </div>
          </div>
          */}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
